/** Wall-overlap resolution: detects when a body overlaps wall tiles and computes the minimum push to clear. */
import Phaser from 'phaser';
import { MAP_HEIGHT_PX, MAP_WIDTH_PX, TILE_SIZE } from '../utils/Constants';
import { worldToTile, tileToWorld } from '../utils/Math';
import type { Position } from '../utils/Types';

/** Sub-pixel overlaps below this threshold are ignored (arcade physics noise). */
const WALL_DEAD_ZONE = 1;

/** Body geometry needed for position checks (avoids coupling to Player). */
export interface BodyGeometry {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
  displayOriginX: number;
  displayOriginY: number;
}

/**
 * Handles collision resolution between arcade physics bodies and tilemap wall tiles.
 * Provides per-frame overlap correction, body-aware walkability checks, and safe-position search.
 */
export class WallPhysicsSystem {
  constructor(private readonly groundLayer: Phaser.Tilemaps.TilemapLayer) {}

  /**
   * Compute the push to resolve all wall tile overlaps for the given body.
   *
   * Per-tile MTV with axis accumulation:
   * 1. For each overlapping tile, find the smallest single-axis push (its MTV).
   * 2. Assign that push to the MTV's axis (X or Y).
   * 3. Per axis, take the largest magnitude push. If opposing pushes exist
   *    on the same axis (sandwich), skip that axis to prevent oscillation.
   *
   * This handles inner corners correctly: corner tiles resolve on one axis,
   * adjacent wall tiles resolve on the other, producing a small 2-axis push
   * instead of a massive single-axis push.
   */
  computeWallCorrection(body: Phaser.Physics.Arcade.Body): { dx: number; dy: number; tiles: string; bodyBounds: string } | null {
    const bodyLeft = body.x;
    const bodyRight = body.x + body.width;
    const bodyTop = body.y;
    const bodyBottom = body.y + body.height;
    const mapW = MAP_WIDTH_PX;
    const mapH = MAP_HEIGHT_PX;

    const tLeft = Math.floor(bodyLeft / TILE_SIZE);
    const tRight = Math.floor((bodyRight - 1) / TILE_SIZE);
    const tTop = Math.floor(bodyTop / TILE_SIZE);
    const tBottom = Math.floor((bodyBottom - 1) / TILE_SIZE);

    // Per-axis push accumulators: track positive and negative pushes separately
    let xPos = 0;  // largest rightward push
    let xNeg = 0;  // largest leftward push (negative)
    let yPos = 0;  // largest downward push
    let yNeg = 0;  // largest upward push (negative)
    let hasXPos = false, hasXNeg = false;
    let hasYPos = false, hasYNeg = false;
    let hasOverlap = false;
    const hitTiles: string[] = [];

    for (let ty = tTop; ty <= tBottom; ty++) {
      for (let tx = tLeft; tx <= tRight; tx++) {
        const tile = this.groundLayer.getTileAt(tx, ty);
        if (!tile || !tile.collides) continue;
        hasOverlap = true;
        hitTiles.push(`(${tx},${ty})`);

        const tileLeft = tx * TILE_SIZE;
        const tileRight = tileLeft + TILE_SIZE;
        const tileTop = ty * TILE_SIZE;
        const tileBottom = tileTop + TILE_SIZE;

        // Push needed in each direction to fully clear this tile
        const pushRight = tileRight - bodyLeft;
        const pushLeft = bodyRight - tileLeft;
        const pushDown = tileBottom - bodyTop;
        const pushUp = bodyBottom - tileTop;

        // Disqualify pushes that would exit the map
        const canRight = (bodyRight + pushRight) <= mapW;
        const canLeft = (bodyLeft - pushLeft) >= 0;
        const canDown = (bodyBottom + pushDown) <= mapH;
        const canUp = (bodyTop - pushUp) >= 0;

        // Pick smallest valid push per axis
        let bestX: number;
        let bestXDir: 'pos' | 'neg';
        if (canRight && canLeft) {
          bestX = Math.min(pushRight, pushLeft);
          bestXDir = pushRight <= pushLeft ? 'pos' : 'neg';
        } else if (canRight) {
          bestX = pushRight;
          bestXDir = 'pos';
        } else if (canLeft) {
          bestX = pushLeft;
          bestXDir = 'neg';
        } else {
          bestX = Math.min(pushRight, pushLeft);
          bestXDir = pushRight <= pushLeft ? 'pos' : 'neg';
        }

        let bestY: number;
        let bestYDir: 'pos' | 'neg';
        if (canDown && canUp) {
          bestY = Math.min(pushDown, pushUp);
          bestYDir = pushDown <= pushUp ? 'pos' : 'neg';
        } else if (canDown) {
          bestY = pushDown;
          bestYDir = 'pos';
        } else if (canUp) {
          bestY = pushUp;
          bestYDir = 'neg';
        } else {
          bestY = Math.min(pushDown, pushUp);
          bestYDir = pushDown <= pushUp ? 'pos' : 'neg';
        }

        // Per-tile MTV: assign to the axis with the smaller push
        if (bestX <= bestY) {
          if (bestXDir === 'pos') {
            hasXPos = true;
            if (pushRight > xPos) xPos = pushRight;
          } else {
            hasXNeg = true;
            if (-pushLeft < xNeg) xNeg = -pushLeft;
          }
        } else {
          if (bestYDir === 'pos') {
            hasYPos = true;
            if (pushDown > yPos) yPos = pushDown;
          } else {
            hasYNeg = true;
            if (-pushUp < yNeg) yNeg = -pushUp;
          }
        }
      }
    }

    if (!hasOverlap) return null;

    const bodyBounds = `body=(${bodyLeft.toFixed(1)},${bodyTop.toFixed(1)})-(${bodyRight.toFixed(1)},${bodyBottom.toFixed(1)}) scan=[${tLeft},${tTop}]-[${tRight},${tBottom}]`;

    // Resolve each axis; skip if sandwiched (opposing pushes)
    let dx = 0;
    if (hasXPos && hasXNeg) {
      // Sandwich on X — skip
    } else if (hasXPos) {
      dx = xPos;
    } else if (hasXNeg) {
      dx = xNeg;
    }

    let dy = 0;
    if (hasYPos && hasYNeg) {
      // Sandwich on Y — skip
    } else if (hasYPos) {
      dy = yPos;
    } else if (hasYNeg) {
      dy = yNeg;
    }

    // Dead zone: ignore sub-pixel overlaps (arcade physics noise)
    if (Math.abs(dx) < WALL_DEAD_ZONE) dx = 0;
    if (Math.abs(dy) < WALL_DEAD_ZONE) dy = 0;

    if (dx === 0 && dy === 0) return null;
    return { dx, dy, tiles: hitTiles.join(' '), bodyBounds };
  }

  /** Check whether a world position falls on a walkable (non-colliding) tile. */
  isPositionWalkable(worldX: number, worldY: number): boolean {
    const tx = worldToTile(worldX);
    const ty = worldToTile(worldY);
    const tile = this.groundLayer.getTileAt(tx, ty);
    return tile != null && !tile.collides;
  }

  /** Check if placing a body at (worldX, worldY) would leave it clear of all wall tiles. */
  isBodyPositionClear(worldX: number, worldY: number, geo: BodyGeometry): boolean {
    const bodyX = worldX - geo.displayOriginX + geo.offsetX;
    const bodyY = worldY - geo.displayOriginY + geo.offsetY;
    const left = bodyX;
    const right = bodyX + geo.width;
    const top = bodyY;
    const bottom = bodyY + geo.height;

    const tLeft = Math.floor(left / TILE_SIZE);
    const tRight = Math.floor((right - 1) / TILE_SIZE);
    const tTop = Math.floor(top / TILE_SIZE);
    const tBottom = Math.floor((bottom - 1) / TILE_SIZE);

    for (let ty = tTop; ty <= tBottom; ty++) {
      for (let tx = tLeft; tx <= tRight; tx++) {
        const tile = this.groundLayer.getTileAt(tx, ty);
        if (!tile || tile.collides) return false;
      }
    }
    return true;
  }

  /** Spiral outward from a position to find a spot where the full body fits. */
  findNearestClearPosition(worldX: number, worldY: number, geo: BodyGeometry): Position | null {
    const cx = worldToTile(worldX);
    const cy = worldToTile(worldY);

    for (let radius = 1; radius <= 20; radius++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;
          const wx = tileToWorld(cx + dx);
          const wy = tileToWorld(cy + dy);
          if (this.isBodyPositionClear(wx, wy, geo)) {
            return { x: wx, y: wy };
          }
        }
      }
    }
    return null;
  }
}
